import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import mammoth from "mammoth";
import * as pdfjsLib from "pdfjs-dist";
import { GoogleGenerativeAI } from "@google/generative-ai";

import * as resumeModel from "../../../models/ResumeParserModel";
import { extractBasicFieldsFromResumeText } from "../../../helpers/ResumeToCandidateHelper";
import { apiError } from "../../../helpers/ErrorHelper";
import { useAuth } from "../../../context/AuthContext";
import { useNotificationCount } from "../../../context/NotificationCountContext";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.149/pdf.worker.min.mjs";

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

export const ResumesForm = ({
  module,
  showModal,
  setShowModal,
  fetchData,
  limit,
  page,
}) => {
  const { state } = useAuth();
  const { setNotificationCount } = useNotificationCount();
  const navigate = useNavigate();

  let user_data = state.user_data;

  const [loading, setLoading] = useState(false);
  const [processingFile, setProcessingFile] = useState(""); // Track current file
  const [errors, setErrors] = useState({});
  const [results, setResults] = useState([]);

  const closeModal = () => {
    setResults([]);
    setErrors({});
    setProcessingFile("");
    setShowModal(false);
  };

  // ---------- Helpers ----------
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });

  const extractTextFromPdf = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let textContent = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const text = await page.getTextContent();
      textContent += text.items.map((s) => s.str).join(" ") + "\n";
    }
    return textContent.trim();
  };

  const extractTextFromDocx = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const { value } = await mammoth.extractRawText({ arrayBuffer });
    return value.trim();
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const isTransientGeminiError = (err) => {
    const code = err?.status ?? err?.statusCode ?? err?.error?.code ?? err?.code;
    const msg = `${err?.message || ""} ${typeof err?.error === "object" ? JSON.stringify(err.error) : ""}`.toLowerCase();
    return (
      code === 429 ||
      code === 503 ||
      msg.includes("429") ||
      msg.includes("503") ||
      msg.includes("resource_exhausted") ||
      msg.includes("unavailable") ||
      msg.includes("high demand") ||
      msg.includes("overloaded") ||
      msg.includes("try again later")
    );
  };

  /** Retries after a pause when Gemini is rate-limited or temporarily overloaded (503). */
  const generateContentWithRetry = async (parts, { maxAttempts = 4, initialDelayMs = 3000 } = {}) => {
    let delayMs = initialDelayMs;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await model.generateContent(parts);
      } catch (err) {
        if (!isTransientGeminiError(err) || attempt === maxAttempts) {
          throw err;
        }
        console.warn(
          `Gemini busy (attempt ${attempt}/${maxAttempts}). Waiting ${delayMs / 1000}s before retry...`
        );
        await sleep(delayMs);
        delayMs *= 2;
      }
    }
  };

  const ocrPdfWithGemini = async (file) => {
    const base64 = await fileToBase64(file);
    const prompt = `Extract structured resume details in strict JSON: {
        "name": "", "email": "", "phone": "", "location": "",
        "education": [], "experience": [], "skills": []
    }`;

    const result = await generateContentWithRetry([
      { inlineData: { data: base64, mimeType: file.type } },
      { text: prompt },
    ]);
    return (await result.response).text();
  };

  function normalizeGeminiResponse(responseText) {
    try {
      const cleaned = responseText.replace(/```json/g, "").replace(/```/g, "").trim();
      const parsed = JSON.parse(cleaned);
      return {
        name: parsed.name || "",
        email: parsed.email || "",
        phone: parsed.phone || "",
        location: parsed.location || "",
        education: parsed.education || [],
        experience: parsed.experience || [],
        skills: parsed.skills || [],
      };
    } catch (err) {
      return { name: "", email: "", phone: "", location: "", education: [], experience: [], skills: [] };
    }
  }

  const isGeminiConfigError = (err) => {
    const msg = `${err?.message || ""} ${JSON.stringify(err?.error || "")}`.toLowerCase();
    return (
      msg.includes("consumer_suspended") ||
      msg.includes("api key") ||
      msg.includes("permission denied") ||
      msg.includes("invalid api key")
    );
  };

  const extractResumeText = async (file) => {
    if (file.type === "application/pdf") {
      return extractTextFromPdf(file);
    }
    if (file.type.includes("wordprocessingml") || file.name.endsWith(".docx")) {
      return extractTextFromDocx(file);
    }
    throw new Error("Unsupported file type");
  };

  const parseResume = async (file) => {
    let text = "";
    try {
      text = await extractResumeText(file);
    } catch (err) {
      return { fileName: file.name, file, data: { name: "" }, error: err.message };
    }

    try {
      if (file.type === "application/pdf" && !text) {
        text = await ocrPdfWithGemini(file);
      }

      const result = await generateContentWithRetry([{
        text: `Extract structured resume details in strict JSON:
          {"name": "", "email": "", "phone": "", "location": "", "education": [], "experience": [], "skills": []}
          Resume content: ${text}`,
      }]);

      const jsonText = (await result.response).text();
      return {
        fileName: file.name,
        file,
        data: normalizeGeminiResponse(jsonText),
        parseMode: "gemini",
      };
    } catch (err) {
      const basicData = extractBasicFieldsFromResumeText(text, file.name);
      if (basicData.name || basicData.email || basicData.phone) {
        return {
          fileName: file.name,
          file,
          data: basicData,
          parseMode: isGeminiConfigError(err) ? "basic-fallback" : "basic-fallback",
          warning: isGeminiConfigError(err)
            ? "Gemini unavailable — saved using basic text extraction."
            : "AI parse failed — saved using basic text extraction.",
        };
      }

      return { fileName: file.name, file, data: { name: "" }, error: err.message };
    }
  };

  // ---------- Sequential Handlers ----------
  const handleUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setLoading(true);
    const tempResults = [];

    for (const file of files) {
      setProcessingFile(file.name); // UI Update: showing current file
      const result = await parseResume(file);
      tempResults.push(result);
      
      // Mandatory wait between files to prevent 429 on free tier
      await sleep(1000); 
    }

    setResults(tempResults);
    setProcessingFile("");
    setLoading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (results.length === 0) return;

    const validResults = results.filter((row) => !row.error && row.data);
    if (!validResults.length) {
      toast.error("No parsed resumes are ready to save.");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("resumes", JSON.stringify(validResults.map((row) => row.data)));
      validResults.forEach((row, index) => {
        if (row.file) {
          formData.append(`resume_file_${index}`, row.file);
        }
      });

      const response = await resumeModel.createWithFiles(user_data.user_token, formData);

      if (response.status) {
        toast.success(response.message);
        const warned = validResults.filter((row) => row.warning);
        if (warned.length) {
          toast.info(`${warned.length} file(s) used basic text extraction instead of Gemini.`);
        }
        closeModal();
        fetchData(limit, page);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);
      if (error.response?.data.invalid_token == 1) navigate("/re-login");
    }
    setLoading(false);
  };

  return (
    <Modal className="cms-modal-form" show={showModal} onHide={closeModal} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Upload & Parse Resumes</Modal.Title>
      </Modal.Header>

      <form onSubmit={submit}>
        <Modal.Body>
          <div className="mb-3">
            <input type="file" accept=".pdf,.doc,.docx" multiple onChange={handleUpload} disabled={loading} />
          </div>

          {loading && (
            <div className="text-primary mb-3">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Parsing: <strong>{processingFile}</strong>...
            </div>
          )}

          {results.length > 0 && (
            <ul className="list-group">
              {results.map((res, idx) => (
                <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                  <span>{res.fileName}</span>
                  {res.error ? (
                    <span className="text-danger">Failed</span>
                  ) : (
                    <span className="text-success">
                      ✔{res.parseMode === "basic-fallback" ? " (basic)" : ""}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" disabled={loading || results.length === 0}>
            {loading ? "Processing..." : "Save All to Backend"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};